import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentTemplate, HeaderTemplate, Progress, Stepper, TitleHeader } from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import VacancyExpiredModal from '../VacancyExpiredModal';
import ParentParticularsAeisExam from '../../../../modules/examRegistration/components/nonNationalExam/ParentParticularsAeisExam';
import NonNationalExamTypeUuidDisplay from '../NonNationalExamTypeUuidDisplay';
import { candidateService } from '../../../../api';
import { useGetParentDetailTypes } from '../../../../api-refactored/non-national-exam-registration/useGetParentDetailTypes';
import { useGetUserCitizenShips } from '../../../../api-refactored/non-national-exam-registration/useGetUserCitizenShips';
import {
  Type,
  useGetWorkPassTypes,
} from '../../../../api-refactored/candidate/useGetWorkPassTypes';
import { IOptionType } from '../../../../api/candidate/exam/model';

const useStyles = makeStyles()((theme) => ({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('md', 'xl')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.down('md')]: {
      left: '-3%',
    },
  },
}));

export interface ParentParticularsLocation {
  examType: string;
  examTitle: string;
  levelSeekingAdmission: IOptionType;
  isSupplementary: boolean;
}

interface ParentParticularsPageProps {
  routeSteps: { label: string }[];
  isOpsAdmin?: boolean;
}

const ParentParticulars = ({ routeSteps, isOpsAdmin }: ParentParticularsPageProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const { state } = useLocation<ParentParticularsLocation>();
  const history = useHistory();
  const examTitle =
    t('exam->>RegisterExam.RegisterForAnExam') +
    (state?.isSupplementary ? ` - ${t('exam->>Instructions.Supplementary')}` : '');

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [successParentDetails, setSuccessParentDetails] = useState<boolean>(false);
  const [successCitizenShips, setSuccessCitizenShips] = useState<boolean>(false);
  const [successWorkPassTypes, setSuccessWorkPassTypes] = useState<boolean>(false);

  const {
    data: parentDetails,
    isLoading: isLoadingParentDetails,
    error: errorParentDetails,
    refetch: refetchParentDetails,
  } = useGetParentDetailTypes({ onSuccess: () => setSuccessParentDetails(true) });

  const { data: citizenShips, refetch: refetchUserCitizenShip } = useGetUserCitizenShips({
    onSuccess: () => {
      setSuccessCitizenShips(true);
    },
  });

  const { data: workPassTypes, refetch: refetchWorkPassType } = useGetWorkPassTypes({
    type: Type.parent,
    onSuccess: () => {
      setSuccessWorkPassTypes(true);
    },
  });

  const {
    data: parentParticularDetails,
    refetch: refetchParentParticularProfile,
  } = candidateService.exam.candidate.useGetParentParticularProfile(applicationUuid, {
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  useEffect(() => {
    refetchParentParticularProfile();
    refetchParentDetails();
    refetchUserCitizenShip();
    refetchWorkPassType();
  }, [
    refetchParentDetails,
    refetchParentParticularProfile,
    refetchWorkPassType,
    refetchUserCitizenShip,
    history.length,
  ]);

  const onCancel = () => {
    history.goBack();
  };
  return (
    <>
      <TitleHeader title={isOpsAdmin ? t('exam->>NonNationalExam.ReviewApplication') : examTitle} />
      <HeaderTemplate>
        <NonNationalExamTypeUuidDisplay nonExamType={state?.examTitle} enableApi={false} />
        <Stepper className={classes.stepper} activeStep={0} steps={routeSteps} />
      </HeaderTemplate>
      {isSuccess && successParentDetails && successWorkPassTypes && successCitizenShips ? (
        <ParentParticularsAeisExam
          isOpsAdmin={isOpsAdmin}
          onCancel={onCancel}
          parentParticularDetails={parentParticularDetails}
          applicationUuid={applicationUuid}
          parentDetails={parentDetails}
          isLoadingParentDetails={isLoadingParentDetails}
          errorParentDetails={errorParentDetails}
          refetchParentDetails={refetchParentDetails}
          workPassTypes={workPassTypes}
          citizenShips={citizenShips}
        />
      ) : (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      )}
      {!isOpsAdmin && <VacancyExpiredModal uuid={applicationUuid} />}
    </>
  );
};

export default ParentParticulars;
